import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, Loader2 } from 'lucide-react';

import { ListPagination } from '@/components/efs/ListPagination';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import { ApiClientError } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { CancelledDismantlingSession } from './types';
import {
  useCancelledDismantlingSessions,
  useRestoreCancelledDismantlingSession,
} from './useDismantling';

interface CancelledDismantlingSessionsDialogProps {
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const impactText = (row: CancelledDismantlingSession) => {
  const counts = row.impact_counts ?? {};
  return [
    counts.input_package_count ?? 0,
    counts.work_entry_count ?? 0,
    counts.dismantling_output_count ?? 0,
    counts.intermediate_count ?? 0,
    counts.inventory_movement_count ?? 0,
  ].join(' / ');
};

export const CancelledDismantlingSessionsDialog = ({
  batchId,
  open,
  onOpenChange,
}: CancelledDismantlingSessionsDialogProps) => {
  const { t } = useTranslation('operations');
  const user = useCurrentUser();
  const canRestoreConfirmed = hasPermission(user, 'processing.dismantling.batch_confirm');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const list = useCancelledDismantlingSessions(
    { monthly_batch_id: batchId, page, per_page: perPage },
    open,
  );
  const restore = useRestoreCancelledDismantlingSession();
  const resetRestore = restore.reset;
  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const meta = list.data?.meta;
  const total = meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageOutOfRange = page > totalPages;
  const restoreBlockers = useMemo(() => {
    if (!(restore.error instanceof ApiClientError)) return [];
    const raw = restore.error.details?.blockers;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const blocker = item as { code?: unknown; count?: unknown };
      return typeof blocker.code === 'string' && typeof blocker.count === 'number'
        ? [{ code: blocker.code, count: blocker.count }]
        : [];
    });
  }, [restore.error]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      resetRestore();
    }
  }, [open, resetRestore]);

  useEffect(() => {
    if (open && !list.isFetching && pageOutOfRange) {
      setPage(totalPages);
    }
  }, [list.isFetching, open, pageOutOfRange, totalPages]);

  return (
    <Dialog open={open} onOpenChange={restore.isPending ? undefined : onOpenChange}>
      <DialogContent className="max-h-[min(90vh,900px)] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dismantling.cancelled_dialog.title', {
              defaultValue: '最近取消した解体',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.dismantling.cancelled_dialog.subtitle', {
              defaultValue:
                '取消済みの解体作業は30日間だけ復元できます。期限後は復元用データが削除されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {list.isLoading || list.isFetching || pageOutOfRange ? (
          <div className="flex items-center gap-2 rounded-md border border-efs-border p-4 text-sm text-efs-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('efs.operations.dismantling.cancelled_dialog.loading', {
              defaultValue: '取消済み解体を読み込んでいます…',
            })}
          </div>
        ) : list.error ? (
          <LocalizedApiError error={list.error} showTechnicalDetails={false} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-efs-border p-6 text-center text-sm text-efs-text-muted">
            {t('efs.operations.dismantling.cancelled_dialog.empty', {
              defaultValue: '復元可能な取消済み解体はありません。',
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-md border border-efs-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.session', {
                        defaultValue: '解体',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.cancelled_at', {
                        defaultValue: '取消日時',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.cancelled_by', {
                        defaultValue: '取消者',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.reason', {
                        defaultValue: '理由',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.impact', {
                        defaultValue: '元P/作業/産出物/中間品/在庫',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.cancelled_dialog.columns.deadline', {
                        defaultValue: '復元期限',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.cancelled_dialog.columns.actions', {
                        defaultValue: '操作',
                      })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const expired = !row.recoverable;
                    const targetLabel =
                      row.package_codes.length > 0
                        ? row.package_codes.join('、')
                        : t('efs.operations.dismantling.cancelled_dialog.target_fallback', {
                            defaultValue: '取消済みの解体作業',
                          });
                    const confirmedPermissionBlocked =
                      row.archived_status === 'confirmed' && !canRestoreConfirmed;
                    const restoreDisabled = confirmedPermissionBlocked || restore.isPending;
                    const restoreDisabledTitle = confirmedPermissionBlocked
                      ? t(
                          'efs.operations.dismantling.cancelled_dialog.restore_confirmed_permission_hint',
                          {
                            defaultValue: '確定済みの解体復元には一括確認権限が必要です。',
                          },
                        )
                      : undefined;
                    return (
                      <TableRow key={row.archive_id}>
                        <TableCell className="max-w-[260px] text-xs">
                          <div
                            className="break-words font-mono font-medium"
                            title={row.dismantling_session_id}
                          >
                            {targetLabel}
                          </div>
                          <div className="mt-1 text-efs-text-muted">
                            {row.dismantling_date ?? '—'} ·{' '}
                            {t(
                              `efs.operations.dismantling.cancelled_dialog.source_kind.${row.source_kind}`,
                              { defaultValue: row.source_kind },
                            )}{' '}
                            →{' '}
                            {t(
                              `efs.operations.dismantling.cancelled_dialog.return_step.${row.return_step}`,
                              { defaultValue: row.return_step },
                            )}
                          </div>
                          <div className="text-efs-text-muted">
                            {t(
                              `efs.operations.dismantling.cancelled_dialog.next_state.${row.next_state}`,
                              { defaultValue: row.next_state },
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatDateTime(row.cancelled_at)}
                        </TableCell>
                        <TableCell className="max-w-[180px] text-xs">
                          <div>{row.cancelled_by_display_name}</div>
                          <div className="truncate text-efs-text-muted">
                            {row.cancelled_by_email}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[260px] text-xs">
                          <div className="line-clamp-3">{row.cancel_reason}</div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{impactText(row)}</TableCell>
                        <TableCell className="text-xs">
                          <div>{formatDateTime(row.purge_after)}</div>
                          <div className={expired ? 'text-destructive' : 'text-efs-text-muted'}>
                            {expired
                              ? t('efs.operations.dismantling.cancelled_dialog.expired', {
                                  defaultValue: '期限切れ',
                                })
                              : t('efs.operations.dismantling.cancelled_dialog.days_left', {
                                  count: row.days_remaining,
                                  defaultValue: 'あと {{count}} 日',
                                })}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {expired ? (
                            <span className="text-xs text-efs-text-muted">
                              {t(
                                'efs.operations.dismantling.cancelled_dialog.restore_unavailable',
                                {
                                  defaultValue: '復元期限を過ぎているため復元できません。',
                                },
                              )}
                            </span>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={restoreDisabled}
                              title={restoreDisabledTitle}
                              onClick={() => restore.mutate(row.archive_id)}
                            >
                              <ArchiveRestore className="mr-1 h-3 w-3" aria-hidden />
                              {t('efs.operations.dismantling.cancelled_dialog.restore', {
                                defaultValue: '復元',
                              })}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <ListPagination
              page={meta?.page ?? page}
              perPage={meta?.per_page ?? perPage}
              total={total}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPerPageChange={(nextPerPage) => {
                setPage(1);
                setPerPage(
                  PAGE_SIZE_OPTIONS.includes(nextPerPage) ? nextPerPage : DEFAULT_PER_PAGE,
                );
              }}
            />
          </div>
        )}

        {restore.error ? (
          <div className="space-y-2">
            <LocalizedApiError error={restore.error} showTechnicalDetails={false} />
            {restoreBlockers.length > 0 ? (
              <ul className="list-disc rounded-md border border-destructive/30 bg-destructive/5 py-2 pl-7 pr-3 text-sm text-destructive">
                {restoreBlockers.map((blocker) => (
                  <li key={blocker.code}>
                    {t(`efs.operations.dismantling.cancel.blockers.${blocker.code}`, {
                      count: blocker.count,
                      defaultValue: t(
                        'efs.operations.dismantling.cancel.blockers.unknown_dependency',
                        {
                          count: blocker.count,
                          defaultValue:
                            '{{count}} 件の関連データがあります。管理者に確認してください。',
                        },
                      ),
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CancelledDismantlingSessionsDialog;
